import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Alert, Button, Checkbox, Input, Modal, Select } from '@/components/ui'
import { ApiError, api } from '@/internationalization/api-client'
import { qk } from '@/internationalization/query-keys'
import { useT } from '@/internationalization/i18n'
import { toast } from '@/stores'
import type { CreateTenantInput, Tenant } from '@/types'

const REGIONS = [
  { value: 'ap-south-1', key: 'createTenant.region.ap' },
  { value: 'eu-west-1', key: 'createTenant.region.eu' },
  { value: 'us-east-1', key: 'createTenant.region.us' },
]

// Names only. The seat and minute limits live in sabha.plans and are applied
// server-side; repeating them here would be a second source of truth for a
// commercial term, and the copy would go stale the first time pricing moved
// without anyone noticing. The response shows the limits that were applied.
const PLANS = [
  { value: 'trial', key: 'createTenant.plan.trial' },
  { value: 'team', key: 'createTenant.plan.team' },
  { value: 'business', key: 'createTenant.plan.business' },
  { value: 'enterprise', key: 'createTenant.plan.enterprise' },
]

const EMPTY: CreateTenantInput = {
  name: '',
  domain: '',
  ownerEmail: '',
  region: 'ap-south-1',
  plan: 'trial',
  allowVerifiedDomainJoin: false,
}

/**
 * Provision a customer workspace.
 *
 * The owner's email is still the field that matters most: the API creates
 * the tenant AND an open invitation for that address, because a workspace with
 * no members has no way to acquire one — the directory admits a new account by
 * matching an unaccepted invitation on email, and a customer's domain is not on
 * the platform allowlist. Getting it wrong does not fail; it produces a
 * workspace nobody can enter, so the field is labelled with what it does rather
 * than with whose address it is.
 *
 * Region and plan are shown because both are awkward to change later — region
 * is where the audio lives, and the storage prefix derived from the domain is
 * immutable once a single recording exists under it.
 */
export function CreateTenantModal({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const t = useT()
  const queryClient = useQueryClient()
  const [form, setForm] = useState<CreateTenantInput>(EMPTY)
  const [problem, setProblem] = useState<string | null>(null)

  const set = <K extends keyof CreateTenantInput>(key: K, value: CreateTenantInput[K]) =>
    setForm((current) => ({ ...current, [key]: value }))

  const create = useMutation({
    mutationFn: (input: CreateTenantInput) => api.post<Tenant>('/admin/tenants', input),
    onSuccess: (tenant) => {
      queryClient.invalidateQueries({ queryKey: qk.admin.tenants })
      // The invitation is the deliverable, so the toast names it rather than
      // confirming the row. Nothing emails it yet — the owner is admitted by
      // signing in with this address.
      toast.success(
        t('createTenant.toast.provisionedTitle', { name: tenant.name }),
        t('createTenant.toast.provisionedBody', {
          who: tenant.ownerEmail ?? t('createTenant.toast.provisionedOwnerFallback'),
        }),
      )
      setForm(EMPTY)
      setProblem(null)
      onClose()
    },
    onError: (error) => {
      // A 409 here is user-correctable and names its field: domain_taken,
      // storage_prefix_taken, unknown_plan. Showing the message beats a generic
      // failure toast, because the fix is in the form.
      setProblem(
        error instanceof ApiError
          ? error.message
          : t('createTenant.couldNotCreate'),
      )
    },
  })

  const domain = form.domain.trim().toLowerCase().replace(/^@/, '')
  const complete = form.name.trim() && domain.includes('.') && form.ownerEmail.includes('@')

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('createTenant.title')}
      description={t('createTenant.subtitle')}
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            isLoading={create.isPending}
            disabled={!complete}
            onClick={() => create.mutate({ ...form, domain })}
          >
            {t('createTenant.title')}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {problem && <Alert tone="danger" title={t('createTenant.failedTitle')}>{problem}</Alert>}

        <Input
          label={t('createTenant.workspaceName')}
          placeholder="Acme India"
          value={form.name}
          onChange={(event) => set('name', event.target.value)}
          required
        />
        <Input
          label={t('createTenant.primaryDomain')}
          placeholder="acme.com"
          hint={t('createTenant.primaryDomainHint')}
          value={form.domain}
          onChange={(event) => set('domain', event.target.value)}
          required
        />
        <Input
          label={t('createTenant.ownerEmail')}
          type="email"
          placeholder="priya@acme.com"
          hint={t('createTenant.ownerEmailHint')}
          value={form.ownerEmail}
          onChange={(event) => set('ownerEmail', event.target.value)}
          required
        />
        <div className="grid gap-4 sm:grid-cols-2">
          <Select
            label={t('createTenant.region')}
            hint={t('createTenant.regionHint')}
            options={REGIONS.map((r) => ({ value: r.value, label: t(r.key) }))}
            value={form.region}
            onChange={(event) => set('region', event.target.value as Tenant['region'])}
          />
          <Select
            label={t('createTenant.plan')}
            hint={t('createTenant.planHint')}
            options={PLANS.map((p) => ({ value: p.value, label: t(p.key) }))}
            value={form.plan}
            onChange={(event) => set('plan', event.target.value as Tenant['plan'])}
          />
        </div>
        <Checkbox
          label={t('createTenant.allowDomainJoin')}
          description={t('createTenant.allowDomainJoinDesc')}
          checked={form.allowVerifiedDomainJoin ?? false}
          onChange={(event) => set('allowVerifiedDomainJoin', event.target.checked)}
        />
      </div>
    </Modal>
  )
}
